import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import LeadDetailDrawer from './LeadDetailDrawer';
import KanbanBoard from './KanbanBoard';
import HotFollowupsWidget from './HotFollowupsWidget';
import DemoFunnelCharts from './DemoFunnelCharts';
import AiLeadsView from './AiLeadsView';
import HotAlertsView from './HotAlertsView';
import LeadCallManager from './LeadCallManager';
import CallLogsView from './CallLogsView';
import DemosView from './DemosView';
import AutomationPanel from './AutomationPanel';
import NotificationsBell from './NotificationsBell';
import { Table2, Columns3, KeyRound, Bot, Flame, Workflow, Download, Facebook, RefreshCw, BarChart3, ChevronDown, ChevronUp, ChevronRight, Search, Send, Building2, X, Phone, PhoneCall, Trash2, RotateCcw, ArrowUp, ArrowDown, ArrowUpDown, GitMerge, CalendarClock } from 'lucide-react';
import { SOURCE_OPTIONS, STATUS_OPTIONS, statusMeta, sourceLabel, inr, ENGAGEMENT_META, ENGAGEMENT_OPTIONS } from './constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TempBadge = ({ t }) => {
  const cfg = t === 'hot' ? { dot: 'bg-rose-500', l: 'Hot' }
    : t === 'warm' ? { dot: 'bg-amber-500', l: 'Warm' }
    : { dot: 'bg-zinc-400', l: 'Cold' };
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-50 border border-zinc-200 text-[11px] font-medium text-zinc-700">
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.l}
    </span>
  );
};

const ScoreRing = ({ score }) => {
  const color = score >= 70 ? '#f43f5e' : score >= 40 ? '#f59e0b' : '#a1a1aa';
  return (
    <div className="relative w-9 h-9 flex items-center justify-center">
      <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="15" fill="none" stroke="#e4e4e7" strokeWidth="3" />
        <circle cx="18" cy="18" r="15" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${(score / 100) * 94.2} 94.2`} strokeLinecap="round" />
      </svg>
      <span className="absolute text-[11px] font-semibold text-zinc-900 tabular-nums">{score}</span>
    </div>
  );
};

const KpiCard = ({ label, value, accent, testid, onClick, active }) => (
  <div onClick={onClick}
    className={`bg-white rounded-xl border p-4 ${accent || 'border-zinc-200'} ${onClick ? 'cursor-pointer hover:shadow-md hover:border-orange-300 transition' : ''} ${active ? 'ring-2 ring-orange-400' : ''}`}
    data-testid={testid}>
    <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-semibold text-zinc-900 mt-1 tabular-nums">{value}</p>
  </div>
);

export default function CRMPro({ token }) {
  const [stats, setStats] = useState(null);
  const [demoFunnel, setDemoFunnel] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectingAll, setSelectingAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // table | board | credentials | ai_leads | hot_alerts
  const [showInsights, setShowInsights] = useState(false); // collapsible KPIs/charts panel
  const [showTasks, setShowTasks] = useState(false); // collapsible Today/Overdue tasks (default बंद)
  const [showAutomation, setShowAutomation] = useState(false);
  const [hotAlertCount, setHotAlertCount] = useState(0);
  const [stuckSummary, setStuckSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // AI chat summaries per lead (cached server-side; fetched lazily for the page)
  const [summaries, setSummaries] = useState({});
  const summariesRef = useRef({});

  // bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('contacted');
  const [bulkTag, setBulkTag] = useState('');

  // filters
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [temperature, setTemperature] = useState('');
  const [sort, setSort] = useState('last_activity_at');
  const [order, setOrder] = useState('desc');
  const [dupGroups, setDupGroups] = useState([]);
  const [showDups, setShowDups] = useState(false);
  const [dupMergingKey, setDupMergingKey] = useState(null);
  const [credStatus, setCredStatus] = useState(''); // '' | sent | unsent | trial (credentials view)
  const [dateRange, setDateRange] = useState(''); // '' (all) | today | week | 15days | month | custom
  const [engagement, setEngagement] = useState(''); // '' | beginner | ready | stalled | demo_sent
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Demo credentials tracking + per-lead actions
  const [expandedCred, setExpandedCred] = useState(null);
  const [showWebhookGuide, setShowWebhookGuide] = useState(false);

  const waStatusBadge = (s) => {
    const map = {
      read: { label: 'Read', dot: 'bg-blue-500' },
      delivered: { label: 'Delivered', dot: 'bg-emerald-500' },
      sent: { label: 'Sent', dot: 'bg-zinc-400' },
      failed: { label: 'Failed', dot: 'bg-rose-500' },
    };
    return map[s] || { label: 'कोई log नहीं', dot: 'bg-zinc-300' };
  };
  const [demoBusyId, setDemoBusyId] = useState(null);
  const [rowBusyId, setRowBusyId] = useState(null);

  const sendDemo = async (lead, e) => {
    if (e) e.stopPropagation();
    const digits = (lead.phone || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { toast.error('इस lead का valid 10-अंकों का WhatsApp नंबर नहीं है'); return; }
    setDemoBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${lead.id}/send-demo`, { method: 'POST', headers });
      const d = await res.json();
      if (d.success) {
        toast.success(d.message || 'Demo credentials भेज दिए ✅');
        setRefreshKey(k => k + 1);
        fetchLeads();
      } else {
        toast.error(d.detail || 'Demo भेजने में दिक्कत');
      }
    } catch (err) { toast.error('Demo भेजने में दिक्कत: ' + err.message); }
    finally { setDemoBusyId(null); }
  };

  // WhatsApp template picker (send ANY approved template, not just demo)
  const [waTemplates, setWaTemplates] = useState([]);
  const [tplMenuId, setTplMenuId] = useState(null);
  const [tplBusyId, setTplBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsapp/templates`, { headers });
        const d = await res.json();
        if (d.success && Array.isArray(d.templates)) {
          setWaTemplates(d.templates.filter(t => String(t.status || '').toUpperCase() === 'APPROVED'));
        }
      } catch (e) { /* templates optional */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendTemplate = async (lead, templateName) => {
    const digits = (lead.phone || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { toast.error('इस lead का valid 10-अंकों का WhatsApp नंबर नहीं है'); return; }
    setTplBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${lead.id}/send-template`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_name: templateName }),
      });
      const d = await res.json();
      if (d.success && d.whatsapp_sent) {
        toast.success(`Template "${templateName.replace(/_/g, ' ')}" भेज दिया ✅`);
        setRefreshKey(k => k + 1); fetchLeads();
      } else {
        const errMsg = Array.isArray(d.detail) ? (d.detail[0]?.msg || 'validation error') : (d.detail || d.message || 'Template भेजने में दिक्कत');
        toast.error(errMsg);
      }
    } catch (err) { toast.error('Template भेजने में दिक्कत: ' + err.message); }
    finally { setTplBusyId(null); }
  };

  const [dripBusy, setDripBusy] = useState(false);
  const [bulkTemplate, setBulkTemplate] = useState('');
  const [tplBulkBusy, setTplBulkBusy] = useState(false);
  const applyBulkTemplate = async () => {
    if (selected.size === 0 || !bulkTemplate) return;
    const count = selected.size;
    if (!window.confirm(`${count} चुने हुए leads को WhatsApp template "${bulkTemplate.replace(/_/g, ' ')}" भेजें?`)) return;
    setTplBulkBusy(true);
    const idsToSend = [...selected];
    const tid = toast.loading(`📤 ${count} leads को भेजना शुरू…`);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/bulk-send-template`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: idsToSend, template_name: bulkTemplate }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) {
        const em = Array.isArray(d.detail) ? (d.detail[0]?.msg || 'error') : (d.detail || 'भेजने में दिक्कत');
        toast.error(em, { id: tid, duration: 8000 });
        setTplBulkBusy(false);
        return;
      }
      // Started in the background — clear selection and poll live progress.
      setSelected(new Set()); setBulkTemplate('');
      const jobId = d.job_id;
      const total = d.total;
      const poll = async () => {
        try {
          const sres = await fetch(`${API_URL}/api/crm/leads/bulk-send-status/${jobId}`, { headers });
          const s = await sres.json();
          if (!s.success) throw new Error('status');
          const finished = s.status === 'done' || s.status === 'error';
          if (!finished) {
            toast.loading(`📤 भेजा जा रहा है… ${s.processed || 0}/${total}`, { id: tid });
            setTimeout(poll, 3000);
          } else {
            if (s.status === 'error') {
              toast.error(`कुछ भेजे गए (${s.sent || 0}/${total}) — बाकी में दिक्कत। Activity log देखें।`, { id: tid, duration: 10000 });
            } else {
              toast.success(`✅ ${s.sent || 0} leads को भेज दिया`, {
                id: tid,
                description: `विफल: ${s.failed || 0} · बिना valid नंबर: ${s.no_phone || 0} · कुल: ${total}`,
                duration: 9000,
              });
            }
            setRefreshKey(k => k + 1); fetchLeads();
            setTplBulkBusy(false);
          }
        } catch (e) {
          toast.message('भेजना background में जारी है — Activity log में status देखें।', { id: tid, duration: 8000 });
          setTplBulkBusy(false);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      toast.error('शुरू करने में दिक्कत: ' + err.message, { id: tid, duration: 8000 });
      setTplBulkBusy(false);
    }
  };
  const bulkDrip = async () => {
    if (!engagement) return;
    const labelMap = { beginner: 'New/Beginner', ready: 'Ready', stalled: 'Stalled', demo_sent: 'Demo भेजे गए' };
    if (!window.confirm(`"${labelMap[engagement] || engagement}" वाले सभी leads को 31-दिन WhatsApp drip campaign में डालें? (रोज़ 1 feature message + CTA)`)) return;
    setDripBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/bulk-drip`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ engagement, language: 'hi' }),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(`✅ ${d.enrolled} leads drip में जोड़े गए` + (d.skipped_active ? ` · ${d.skipped_active} पहले से active` : '') + (d.no_phone ? ` · ${d.no_phone} बिना नंबर` : ''));
      } else {
        toast.error(d.detail || 'Drip enroll नहीं हो पाया');
      }
    } catch (err) { toast.error('Drip enroll में दिक्कत: ' + err.message); }
    finally { setDripBusy(false); }
  };


  const quickChangeStatus = async (lead, newStatus, e) => {
    if (e) e.stopPropagation();
    if (newStatus === lead.status) return;
    setRowBusyId(lead.id);
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
    try {
      await fetch(`${API_URL}/api/crm/leads/bulk-status`, {
        method: 'POST', headers, body: JSON.stringify({ ids: [lead.id], status: newStatus }),
      });
      toast.success('Stage बदल गया → ' + statusMeta(newStatus).label);
      setRefreshKey(k => k + 1);
    } catch (err) { toast.error('Stage बदलने में दिक्कत'); }
    finally { setRowBusyId(null); }
  };


  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/stats`, { headers });
      const d = await res.json();
      if (d.success) setStats(d);
    } catch (e) { console.error('crm stats', e); }
  }, [token]);

  const fetchDemoFunnel = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/demo-funnel`, { headers });
      const d = await res.json();
      if (d.success) setDemoFunnel(d);
    } catch (e) { console.error('crm demo-funnel', e); }
  }, [token]);

  const fetchDuplicates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/duplicates`, { headers });
      const d = await res.json();
      if (d.success) setDupGroups(d.groups || []);
    } catch (e) { console.error('crm duplicates', e); }
  }, [token]);

  const fetchTodayTasks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/tasks?filter=today_overdue`, { headers });
      const d = await res.json();
      if (d.success) setTodayTasks(d.tasks || []);
    } catch (e) { console.error('crm tasks', e); }
  }, [token]);

  const completeTodayTask = async (taskId) => {
    try {
      await fetch(`${API_URL}/api/crm/tasks/${taskId}/complete`, { method: 'POST', headers });
      await fetchTodayTasks();
    } catch (e) { console.error('complete task', e); }
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const isTrash = viewMode === 'trash';
    try {
      const params = new URLSearchParams({ page, page_size: pageSize, sort: isTrash && sort === 'last_activity_at' ? 'deleted_at' : sort, order });
      if (isTrash) params.set('deleted', 'true');
      if (q) params.set('q', q);
      if (source) params.set('source', source);
      if (status) params.set('status', status);
      if (temperature) params.set('temperature', temperature);
      if (credStatus) params.set('cred', credStatus);
      if (engagement) params.set('engagement', engagement);
      if (dateRange) {
        params.set('date_range', dateRange);
        if (dateRange === 'custom') {
          if (dateFrom) params.set('date_from', dateFrom);
          if (dateTo) params.set('date_to', dateTo);
        }
      }
      const res = await fetch(`${API_URL}/api/crm/leads?${params}`, { headers });
      const d = await res.json();
      if (d.success) { setLeads(d.leads || []); setTotal(d.total); setPages(d.pages || 1); }
    } catch (e) { console.error('crm leads', e); }
    finally { setLoading(false); }
  }, [token, page, pageSize, q, source, status, temperature, sort, order, credStatus, dateRange, dateFrom, dateTo, engagement, viewMode]);

  // Select ALL leads matching the current filter (across pages), capped at 500.
  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const params = new URLSearchParams({ ids_only: 'true', sort, order });
      if (viewMode === 'trash') params.set('deleted', 'true');
      if (q) params.set('q', q);
      if (source) params.set('source', source);
      if (status) params.set('status', status);
      if (temperature) params.set('temperature', temperature);
      if (credStatus) params.set('cred', credStatus);
      if (engagement) params.set('engagement', engagement);
      if (dateRange) {
        params.set('date_range', dateRange);
        if (dateRange === 'custom') {
          if (dateFrom) params.set('date_from', dateFrom);
          if (dateTo) params.set('date_to', dateTo);
        }
      }
      const res = await fetch(`${API_URL}/api/crm/leads?${params}`, { headers });
      const d = await res.json();
      if (d.success) {
        setSelected(new Set(d.ids || []));
        if (d.capped) toast.warning(`${d.total} में से पहले 500 चुने गए (एक बार में ज़्यादा से ज़्यादा 500 भेज सकते हैं)`);
        else toast.success(`${(d.ids || []).length} leads चुने गए ✅`);
      } else toast.error('Select all विफल');
    } catch (e) { toast.error('Select all विफल'); }
    finally { setSelectingAll(false); }
  };

  const fetchSummaries = useCallback(async (list) => {
    const ids = (list || []).filter(l => !l.ai_chat_summary && !summariesRef.current[l.id]).map(l => l.id);
    if (!ids.length) return;
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/ai-summaries`, {
        method: 'POST', headers, body: JSON.stringify({ lead_ids: ids }),
      });
      const d = await res.json();
      if (d.success) {
        summariesRef.current = { ...summariesRef.current, ...d.summaries };
        setSummaries(s => ({ ...s, ...d.summaries }));
        if (d.capped) setTimeout(() => fetchSummaries(list), 1500);
      }
    } catch (e) { console.error('ai-summaries', e); }
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats, refreshKey]);

  // Poll pending hot-alert count for the view-switcher badge
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsapp/hot-leads?status=pending&limit=1`, { headers });
        const d = await res.json();
        if (active && d.success) setHotAlertCount(d.pending || 0);
      } catch (e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { active = false; clearInterval(t); };
  }, [token]);

  // Live Stuck-Leads + total counters — auto-updates every 30s.
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/crm/stuck-summary`, { headers });
        const d = await res.json();
        if (active && d.success) setStuckSummary(d);
      } catch (e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, [token, refreshKey]);
  useEffect(() => { fetchDemoFunnel(); }, [fetchDemoFunnel, refreshKey]);
  useEffect(() => { fetchTodayTasks(); }, [fetchTodayTasks, refreshKey]);
  useEffect(() => { if (viewMode === 'table' || viewMode === 'credentials' || viewMode === 'trash') fetchLeads(); }, [fetchLeads, viewMode]);
  useEffect(() => { if (viewMode === 'table' || viewMode === 'credentials') fetchDuplicates(); }, [fetchDuplicates, viewMode, refreshKey]);
  useEffect(() => { if (leads.length) fetchSummaries(leads); }, [leads, fetchSummaries]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [q, source, status, temperature, sort, order, credStatus]);

  const runSync = async () => {
    setSyncing(true);
    try {
      await fetch(`${API_URL}/api/crm/sync`, { method: 'POST', headers });
      bumpRefresh();
    } catch (e) { console.error('sync', e); }
    finally { setSyncing(false); }
  };

  const [metaSyncing, setMetaSyncing] = useState(false);
  const syncMetaLeads = async () => {
    setMetaSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/sync-leads`, { method: 'POST', headers });
      const d = await res.json();
      if (d.success) {
        toast.success(d.message || `${d.stats?.new || 0} नए Meta lead आए ✅`);
      } else {
        toast.error(`Meta sync नहीं हो पाया: ${d.stats?.error || d.message || 'अनजान त्रुटि'}`);
      }
      bumpRefresh();
    } catch (e) {
      toast.error('Meta sync में दिक्कत आई');
      console.error('meta sync', e);
    } finally { setMetaSyncing(false); }
  };

  const bumpRefresh = () => { setRefreshKey(k => k + 1); fetchStats(); if (viewMode === 'table' || viewMode === 'credentials' || viewMode === 'trash') fetchLeads(); };
  const onLeadChanged = () => bumpRefresh();

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleSelectAll = () => {
    setSelected(prev => prev.size === leads.length ? new Set() : new Set(leads.map(l => l.id)));
  };

  const applyBulkStatus = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/bulk-status`, {
        method: 'POST', headers, body: JSON.stringify({ ids: [...selected], status: bulkStatus }) });
      setSelected(new Set());
      bumpRefresh();
    } finally { setBulkBusy(false); }
  };

  const applyBulkTag = async () => {
    if (selected.size === 0 || !bulkTag.trim()) return;
    setBulkBusy(true);
    try {
      await fetch(`${API_URL}/api/crm/leads/bulk-tag`, {
        method: 'POST', headers, body: JSON.stringify({ ids: [...selected], tags: [bulkTag.trim()] }) });
      setBulkTag('');
      setSelected(new Set());
      bumpRefresh();
    } finally { setBulkBusy(false); }
  };

  const toggleSort = (field) => {
    if (sort === field) setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(field); setOrder('desc'); }
    setPage(1);
  };

  const mergeLeads = async () => {
    const ids = [...selected];
    if (ids.length < 2) { toast.error('Merge के लिए कम से कम 2 leads चुनें'); return; }
    if (!window.confirm(`${ids.length} leads को एक में merge करें?\n\n• Phone number रखा जाएगा\n• सबसे ऊँचा (Hot) score व furthest stage रहेगा\n• सारी history, sources व chat एक lead में आ जाएँगे\n• बाकी duplicate Trash में चले जाएँगे`)) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/merge`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }) });
      const d = await res.json();
      if (d.success) { toast.success(`${d.merged} duplicate lead merge हो गए 🔗`); setSelected(new Set()); bumpRefresh(); }
      else toast.error(d.detail || 'Merge नहीं हो पाया');
    } catch (err) { toast.error('Merge में दिक्कत: ' + err.message); }
    finally { setBulkBusy(false); }
  };

  const mergeGroup = async (group) => {
    const ids = (group.leads || []).map(l => l.id);
    if (ids.length < 2) return;
    if (!window.confirm(`"${group.name}" के ${ids.length} leads को एक में merge करें?\n\n• Phone number रखा जाएगा\n• सबसे ऊँचा (Hot) score व furthest stage रहेगा\n• सारी history एक lead में आ जाएगी; बाकी Trash में जाएँगे`)) return;
    setDupMergingKey(group.key);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/merge`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }) });
      const d = await res.json();
      if (d.success) { toast.success(`"${group.name}" के ${d.merged} duplicate merge हो गए 🔗`); bumpRefresh(); }
      else toast.error(d.detail || 'Merge नहीं हो पाया');
    } catch (err) { toast.error('Merge में दिक्कत: ' + err.message); }
    finally { setDupMergingKey(null); }
  };

  const deleteLead = async (lead, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`"${lead.name || lead.phone}" lead को delete करें? (छिप जाएगा)`)) return;
    setRowBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${lead.id}`, { method: 'DELETE', headers });
      const d = await res.json();
      if (d.success) { toast.success('Lead delete हो गया 🗑️'); fetchLeads(); }
      else toast.error('Delete नहीं हो पाया');
    } catch (err) { toast.error('Delete में दिक्कत: ' + err.message); }
    finally { setRowBusyId(null); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} चुने हुए leads delete करें?`)) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/bulk-delete`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selected] }) });
      const d = await res.json();
      if (d.success) toast.success(`🗑️ ${d.deleted} leads delete हो गए`);
      setSelected(new Set());
      bumpRefresh();
    } catch (err) { toast.error('Delete में दिक्कत: ' + err.message); }
    finally { setBulkBusy(false); }
  };

  const restoreLead = async (lead, e) => {
    if (e) e.stopPropagation();
    setRowBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${lead.id}/restore`, { method: 'POST', headers });
      const d = await res.json();
      if (d.success) { toast.success('Lead वापस आ गया ♻️'); fetchLeads(); }
      else toast.error('Restore नहीं हो पाया');
    } catch (err) { toast.error('Restore में दिक्कत: ' + err.message); }
    finally { setRowBusyId(null); }
  };

  const permanentDeleteLead = async (lead, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`"${lead.name || lead.phone}" को हमेशा के लिए delete करें? यह वापस नहीं आ सकता ⚠️`)) return;
    setRowBusyId(lead.id);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${lead.id}/permanent`, { method: 'DELETE', headers });
      const d = await res.json();
      if (d.success) { toast.success('हमेशा के लिए delete हो गया'); fetchLeads(); }
      else toast.error('Permanent delete नहीं हो पाया');
    } catch (err) { toast.error('दिक्कत: ' + err.message); }
    finally { setRowBusyId(null); }
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/bulk-restore`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selected] }) });
      const d = await res.json();
      if (d.success) toast.success(`♻️ ${d.restored} leads वापस आ गए`);
      setSelected(new Set());
      bumpRefresh();
    } catch (err) { toast.error('Restore में दिक्कत: ' + err.message); }
    finally { setBulkBusy(false); }
  };

  const bulkPermanentDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} leads को हमेशा के लिए delete करें? यह वापस नहीं आ सकता ⚠️`)) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/bulk-permanent-delete`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selected] }) });
      const d = await res.json();
      if (d.success) toast.success(`${d.deleted} leads हमेशा के लिए delete हो गए`);
      setSelected(new Set());
      bumpRefresh();
    } catch (err) { toast.error('दिक्कत: ' + err.message); }
    finally { setBulkBusy(false); }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (source) params.set('source', source);
    if (status) params.set('status', status);
    if (temperature) params.set('temperature', temperature);
    try {
      const res = await fetch(`${API_URL}/api/crm/export?${params}`, { headers });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `crm_leads_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('export', e); }
  };

  const isLeadView = viewMode === 'table' || viewMode === 'board' || viewMode === 'credentials';
  const isTrash = viewMode === 'trash';

  return (
    <div className="space-y-5" data-testid="crm-pro-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">Lead CRM</h2>
          <p className="text-zinc-500 text-sm mt-0.5">सभी sources एक जगह · Timeline · Pipeline · Forecast</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <NotificationsBell />
          <button onClick={() => setShowAutomation(true)} data-testid="crm-automation-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-200 text-zinc-700 text-sm font-medium hover:bg-zinc-50">
            <Workflow className="w-4 h-4" /> Automation
          </button>
          <button onClick={exportCsv} data-testid="crm-export-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-200 text-zinc-700 text-sm font-medium hover:bg-zinc-50">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={syncMetaLeads} disabled={metaSyncing} data-testid="crm-meta-sync-btn"
            title="Facebook/Instagram Instant-Form leads को CRM में लाएँ"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-200 text-zinc-700 text-sm font-medium hover:bg-zinc-50 disabled:opacity-50">
            <Facebook className="w-4 h-4" /> {metaSyncing ? 'Meta…' : 'Meta'}
          </button>
          <button onClick={runSync} disabled={syncing} data-testid="crm-sync-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sync…' : 'Sync'}
          </button>
        </div>
      </div>

      {/* Live counters — Total leads · Conversion Journey · Stuck leads (auto-updates every 30s) */}
      {stuckSummary && (
        <div className="flex flex-wrap items-stretch gap-2" data-testid="crm-stuck-summary">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-zinc-200" data-testid="stuck-total-leads">
            <span className="text-[11px] text-zinc-500 font-medium">कुल Leads</span>
            <span className="text-base font-bold text-zinc-900 tabular-nums">{stuckSummary.total_leads}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-50 border border-teal-200" data-testid="stuck-cj-leads">
            <span className="text-[11px] text-teal-600 font-medium">🚀 Conversion Journey</span>
            <span className="text-base font-bold text-teal-700 tabular-nums">{stuckSummary.conversion_journey}</span>
          </div>
          <button
            onClick={() => { setViewMode('board'); }}
            data-testid="stuck-leads-widget"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${stuckSummary.stuck_total > 0 ? 'bg-amber-50 border-amber-300 hover:bg-amber-100' : 'bg-white border-zinc-200'}`}>
            <span className={`text-[11px] font-medium ${stuckSummary.stuck_total > 0 ? 'text-amber-700' : 'text-zinc-500'}`}>⚠️ अटके Leads</span>
            <span className={`text-base font-bold tabular-nums ${stuckSummary.stuck_total > 0 ? 'text-amber-700' : 'text-zinc-700'}`}>{stuckSummary.stuck_total}</span>
            {stuckSummary.stuck_total > 0 && (
              <span className="text-[10px] text-amber-600 hidden sm:inline">
                (🎬 {stuckSummary.stuck_by_stage.demo} · 📤 {stuckSummary.stuck_by_stage.data} · 💳 {stuckSummary.stuck_by_stage.payment_link})
              </span>
            )}
          </button>
        </div>
      )}

      {/* View switcher — segmented control (horizontally scrollable on mobile) */}
      <div className="relative md:contents">
      <div className="flex items-center gap-1 bg-zinc-100/80 p-1 rounded-lg border border-zinc-200/60 max-w-full overflow-x-auto md:w-fit no-scrollbar -mx-1 px-2 md:mx-0 md:px-1" data-testid="crm-view-switcher">
        {[
          { key: 'table', label: 'Table', Icon: Table2 },
          { key: 'board', label: 'Pipeline', Icon: Columns3 },
          { key: 'credentials', label: 'Credentials', Icon: KeyRound },
          { key: 'ai_leads', label: 'AI Leads', Icon: Bot },
          { key: 'hot_alerts', label: 'Hot Alerts', Icon: Flame, count: hotAlertCount },
          { key: 'demos', label: 'Demos', Icon: CalendarClock },
          { key: 'calls', label: 'Calls', Icon: Phone },
          { key: 'call_logs', label: 'Call Logs', Icon: PhoneCall },
          { key: 'trash', label: 'Trash', Icon: Trash2 },
        ].map(({ key, label, Icon, count }) => (
          <button key={key} onClick={() => setViewMode(key)} data-testid={`crm-viewmode-${key}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${viewMode === key ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}>
            <Icon className="w-4 h-4" strokeWidth={2} /> {label}
            {count > 0 && <span className="ml-0.5 min-w-[18px] h-[18px] px-1 bg-rose-600 text-white text-[10px] font-semibold rounded-full inline-flex items-center justify-center tabular-nums">{count}</span>}
          </button>
        ))}
      </div>
      {/* mobile scroll hint: fade + chevron on the right edge */}
      <div className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-9 bg-gradient-to-l from-zinc-100 to-transparent rounded-r-lg flex items-center justify-end pr-1">
        <ChevronRight className="w-4 h-4 text-zinc-400 animate-pulse" />
      </div>
      </div>

      {/* Today's & Overdue Tasks (collapsible — default बंद, ज़रूरत पर expand) */}
      {isLeadView && todayTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="crm-today-tasks">
          <button onClick={() => setShowTasks(s => !s)} data-testid="crm-today-tasks-toggle"
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50">
            <div className="flex items-center gap-2.5">
              <span className="text-base">🔔</span>
              <div>
                <p className="font-semibold text-zinc-900 text-sm">आज के / Overdue Tasks</p>
                <p className="text-[11px] text-zinc-400 tabular-nums">{todayTasks.length} pending · देखने के लिए tap करें</p>
              </div>
            </div>
            {showTasks ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </button>
          {showTasks && (
            <div className="px-4 pb-4 space-y-2 border-t border-zinc-100 pt-3 max-h-96 overflow-y-auto" data-testid="crm-today-tasks-list">
              {todayTasks.map((t) => (
                <div key={t.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${t.is_overdue ? 'bg-rose-50 border-rose-100' : 'bg-amber-50 border-amber-100'}`} data-testid={`today-task-${t.id}`}>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setSelectedId(t.lead_id)}>
                    <p className="text-sm text-zinc-700 truncate">{t.type_label} · {t.title}</p>
                    <p className={`text-[10px] ${t.is_overdue ? 'text-rose-600 font-medium' : 'text-zinc-500'}`}>
                      {t.is_overdue ? 'Overdue · ' : 'आज · '}{t.lead_name} · {t.due_at ? new Date(t.due_at).toLocaleString('hi-IN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                  <button onClick={() => completeTodayTask(t.id)} data-testid={`today-task-complete-${t.id}`}
                    className="shrink-0 text-[11px] px-2.5 py-1 rounded-md bg-zinc-900 text-white hover:bg-zinc-800">Done</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Insights — KPIs + Demo Attribution (collapsible, default बंद ताकि leads पहले दिखें) */}
      {isLeadView && (
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="crm-insights-panel">
        <button onClick={() => setShowInsights(s => !s)} data-testid="crm-insights-toggle"
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50">
          <div className="flex items-center gap-2.5">
            <BarChart3 className="w-4 h-4 text-zinc-500" strokeWidth={2} />
            <div>
              <p className="font-semibold text-zinc-900 text-sm">Insights — आँकड़े व Demo Attribution</p>
              <p className="text-[11px] text-zinc-400">{stats ? `कुल ${stats.total} leads · ${stats.hot} hot · Conversion ${stats.conversion_rate}%` : 'KPIs, charts व breakdown'}</p>
            </div>
          </div>
          {showInsights ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
        </button>
        {showInsights && (
          <div className="px-4 pb-4 space-y-4 border-t border-zinc-100 pt-4">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <KpiCard label="कुल Leads" value={stats.total} accent="border-zinc-300" testid="kpi-total" />
                <KpiCard label="Hot Leads" value={stats.hot} accent="border-rose-400" testid="kpi-hot" />
                <KpiCard label="🎬 Demo Scheduled" value={(stats.funnel && stats.funnel.demo_scheduled) || 0}
                  accent="border-orange-400" testid="kpi-demo-scheduled"
                  active={status === 'demo_scheduled'}
                  onClick={() => { setStatus(status === 'demo_scheduled' ? '' : 'demo_scheduled'); setPage(1); setViewMode('table'); }} />
                <KpiCard label="Open Forecast" value={inr(stats.open_forecast)} accent="border-zinc-300" testid="kpi-forecast" />
                <KpiCard label="Won Revenue" value={inr(stats.won_revenue)} accent="border-emerald-400" testid="kpi-won" />
                <KpiCard label="Conversion" value={`${stats.conversion_rate}%`} accent="border-zinc-300" testid="kpi-conversion" />
              </div>
            )}

            {/* Demo Source Attribution — visual charts */}
            <DemoFunnelCharts funnel={demoFunnel} />

            {/* Demo Source Funnel — detailed breakdown table */}
            {demoFunnel && demoFunnel.totals.demos > 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="crm-demo-funnel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-zinc-900">Detailed Breakdown</p>
                  <span className="text-[11px] text-zinc-400">हर source के exact आँकड़े</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-400 border-b border-zinc-100">
                        <th className="py-2 pr-3">Source</th>
                        <th className="py-2 px-3 text-center">Demos</th>
                        <th className="py-2 px-3 text-center">Demo Done</th>
                        <th className="py-2 px-3 text-center">Activation</th>
                        <th className="py-2 px-3 text-center">Won</th>
                        <th className="py-2 pl-3 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoFunnel.sources.map((s) => (
                        <tr key={s.source} className="border-b border-zinc-50" data-testid={`demo-funnel-row-${s.source}`}>
                          <td className="py-2 pr-3 font-medium text-zinc-700">{s.label}</td>
                          <td className="py-2 px-3 text-center text-zinc-600 tabular-nums">{s.demos}</td>
                          <td className="py-2 px-3 text-center text-zinc-900 font-semibold tabular-nums">{s.demo_done}</td>
                          <td className="py-2 px-3 text-center text-zinc-900 font-semibold tabular-nums">{s.activation}</td>
                          <td className="py-2 px-3 text-center text-emerald-600 font-semibold tabular-nums">{s.won}</td>
                          <td className="py-2 pl-3 text-right text-zinc-900 font-semibold tabular-nums">{inr(s.revenue)}</td>
                        </tr>
                      ))}
                      <tr className="bg-zinc-50 font-semibold text-zinc-900" data-testid="demo-funnel-total">
                        <td className="py-2 pr-3">कुल</td>
                        <td className="py-2 px-3 text-center tabular-nums">{demoFunnel.totals.demos}</td>
                        <td className="py-2 px-3 text-center tabular-nums">{demoFunnel.totals.demo_done}</td>
                        <td className="py-2 px-3 text-center tabular-nums">{demoFunnel.totals.activation}</td>
                        <td className="py-2 px-3 text-center tabular-nums">{demoFunnel.totals.won}</td>
                        <td className="py-2 pl-3 text-right tabular-nums">{inr(demoFunnel.totals.revenue)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}


      {/* Today's Hot Follow-ups */}
      {isLeadView && <HotFollowupsWidget token={token} onOpenLead={setSelectedId} refreshKey={refreshKey} />}

      {viewMode === 'ai_leads' ? (
        <AiLeadsView token={token} />
      ) : viewMode === 'demos' ? (
        <DemosView token={token} />
      ) : viewMode === 'hot_alerts' ? (
        <HotAlertsView token={token} onCountChange={setHotAlertCount} />
      ) : viewMode === 'calls' ? (
        <LeadCallManager token={token} onOpenLead={setSelectedId} />
      ) : viewMode === 'call_logs' ? (
        <CallLogsView token={token} onOpenLead={setSelectedId} />
      ) : viewMode === 'board' ? (
        <KanbanBoard token={token} onOpenLead={setSelectedId} refreshKey={refreshKey} />
      ) : viewMode === 'credentials' ? (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="crm-credentials-view">
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-sm font-semibold text-zinc-900">Credentials — सभी Leads ({total})</p>
              <p className="text-[11px] text-zinc-500">हर lead (Hot/Warm/Cold) को यहीं से login credentials भेजें — demo हो चुका हो तो आगे बढ़ाने के लिए ज़रूरी।</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { key: '', label: 'सभी' },
                { key: 'unsent', label: 'नहीं भेजे' },
                { key: 'sent', label: 'भेजे' },
                { key: 'trial', label: 'Active Trial' },
              ].map(t => (
                <button key={t.key || 'all'} onClick={() => setCredStatus(t.key)} data-testid={`crm-cred-filter-${t.key || 'all'}`}
                  className={`text-xs px-3 py-1.5 rounded-md font-medium border ${credStatus === t.key ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
                  {t.label}
                </button>
              ))}
              <button onClick={() => setShowWebhookGuide(s => !s)} data-testid="crm-webhook-guide-toggle"
                className="text-xs px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-600 bg-white hover:bg-zinc-50 font-medium">
                {showWebhookGuide ? 'Guide बंद' : 'Delivered/Read Setup'}
              </button>
            </div>
          </div>

          {/* Shared filter bar — search / source / status / temperature */}
          <div className="px-4 py-3 border-b border-zinc-200 bg-white flex flex-wrap items-center gap-2" data-testid="crm-cred-filter-bar">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-cred-search-input"
                placeholder="नाम / फ़ोन / स्कूल / शहर खोजें…"
                className="w-full h-9 pl-9 pr-3 border border-zinc-200 rounded-md text-sm focus:ring-2 focus:ring-zinc-200 focus:border-zinc-300 outline-none" />
            </div>
            <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="crm-cred-filter-source"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="crm-cred-filter-status"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              <option value="">सभी Status</option>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={temperature} onChange={(e) => setTemperature(e.target.value)} data-testid="crm-cred-filter-temp"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              <option value="">सभी Temp</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
          </div>

          {/* Meta webhook setup guide */}
          {showWebhookGuide && (
            <div className="px-5 py-4 border-b border-zinc-200 bg-zinc-50 text-sm text-zinc-700 space-y-3" data-testid="crm-webhook-guide">
              <p className="font-semibold text-zinc-900">Delivered/Read टिक चालू करने के लिए — Meta में Webhook जोड़ें (2 step)</p>
              <p className="text-xs text-zinc-600 bg-white border border-zinc-200 rounded-lg p-2">अभी "Sent" तो दिखता है (Meta ने message स्वीकार किया), पर "Delivered / Read" तभी आएँगे जब नीचे दी गई setup पूरी हो।</p>
              <div className="space-y-1">
                <p className="font-medium">Step 1 — Meta App Dashboard में जाएँ:</p>
                <p className="text-xs text-zinc-600">developers.facebook.com → आपका App → <b>WhatsApp → Configuration → Webhook</b> → "Edit" पर click करें और ये भरें:</p>
                <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-2 mt-1">
                  <div className="flex items-center justify-between gap-2">
                    <div><span className="text-[11px] text-zinc-500">Callback URL</span><br /><span className="font-mono text-xs break-all">https://shiksha-mitra.com/api/whatsapp/webhook</span></div>
                    <button onClick={() => { navigator.clipboard.writeText('https://shiksha-mitra.com/api/whatsapp/webhook'); toast.success('URL copy हो गया'); }} className="text-xs px-2 py-1 rounded-md border border-zinc-200 hover:bg-zinc-50 whitespace-nowrap" data-testid="crm-webhook-copy-url">Copy</button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div><span className="text-[11px] text-zinc-500">Verify Token</span><br /><span className="font-mono text-xs break-all">shiksha_mitra_wh_verify_2026</span></div>
                    <button onClick={() => { navigator.clipboard.writeText('shiksha_mitra_wh_verify_2026'); toast.success('Token copy हो गया'); }} className="text-xs px-2 py-1 rounded-md border border-zinc-200 hover:bg-zinc-50 whitespace-nowrap" data-testid="crm-webhook-copy-token">Copy</button>
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <p className="font-medium">Step 2 — "Verify and Save" दबाएँ, फिर Webhook fields में <b>"messages"</b> के सामने <b>Subscribe</b> कीजिए।</p>
                <p className="text-xs text-zinc-600">बस! अब हर credential message का असली Delivered/Read status अपने-आप यहाँ update होने लगेगा।</p>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>{['Lead', 'Source', 'Login ID', 'Password', 'भेजा गया', 'Trial', 'Delivery', 'Action'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center"><div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-zinc-200 border-t-zinc-500"></div></td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-zinc-400" data-testid="crm-creds-empty">इस filter में कोई lead नहीं</td></tr>
                ) : leads.map((l) => (
                  <React.Fragment key={l.id}>
                  <tr data-testid={`crm-cred-row-${l.id}`} className="hover:bg-zinc-50">
                    <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedId(l.id)}>
                      <p className="font-medium text-zinc-900">{l.name || 'Lead'}</p>
                      <p className="text-xs text-zinc-500 tabular-nums">{l.phone || '—'}</p>
                      {l.school_name && <p className="text-[11px] text-zinc-400 truncate max-w-[180px] flex items-center gap-1"><Building2 className="w-3 h-3 shrink-0" /> {l.school_name}</p>}
                      <span className="inline-block mt-1"><TempBadge t={l.temperature} /></span>
                    </td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-md bg-zinc-100 text-zinc-600 whitespace-nowrap">{sourceLabel(l.source)}</span></td>
                    <td className="px-4 py-3 text-zinc-600 tabular-nums">{l.demo_phone || '—'}</td>
                    <td className="px-4 py-3 font-mono text-zinc-700">
                      <div>{l.demo_password || '—'}</div>
                      {l.login_ok === true && (
                        <span data-testid={`crm-cred-loginok-${l.id}`}
                          className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          🔑 Login OK
                        </span>
                      )}
                      {l.login_ok === false && (
                        <span data-testid={`crm-cred-loginfail-${l.id}`} title="भेजा गया password काम नहीं करेगा — 'दोबारा भेजें' दबाएँ, अपने-आप ठीक हो जाएगा"
                          className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          🔑 Login Fail — दोबारा भेजें
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums">{l.demo_sent_at ? new Date(l.demo_sent_at).toLocaleString('hi-IN') : '—'}</td>
                    <td className="px-4 py-3">
                      {l.trial_started ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-zinc-50 border border-zinc-200 text-zinc-700" data-testid={`crm-cred-trial-${l.id}`} title={l.last_login ? new Date(l.last_login).toLocaleString('hi-IN') : ''}><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active</span>
                      ) : l.demo_sent ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-zinc-50 border border-zinc-200 text-zinc-700" data-testid={`crm-cred-trial-${l.id}`}><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> शुरू नहीं</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-zinc-50 border border-zinc-200 text-zinc-400" data-testid={`crm-cred-trial-${l.id}`}><span className="w-1.5 h-1.5 rounded-full bg-zinc-300" /> नहीं भेजे</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-zinc-50 border border-zinc-200 text-zinc-700" data-testid={`crm-cred-wa-${l.id}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${waStatusBadge(l.wa_status).dot}`} />{waStatusBadge(l.wa_status).label}
                        </span>
                        {l.wa_message_id && (
                          <button onClick={() => setExpandedCred(expandedCred === l.id ? null : l.id)}
                            data-testid={`crm-cred-view-${l.id}`}
                            className="text-[11px] text-zinc-600 hover:text-zinc-900 underline">देखें</button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={(e) => sendDemo(l, e)} disabled={demoBusyId === l.id}
                          data-testid={`crm-cred-send-${l.id}`}
                          title="14-दिन free trial के login credentials WhatsApp पर भेजें"
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800 disabled:opacity-50 whitespace-nowrap">
                          {demoBusyId === l.id ? <span className="animate-spin">…</span> : <Send className="w-3.5 h-3.5" />} {l.demo_sent ? 'दोबारा भेजें' : 'Credentials भेजें'}
                        </button>
                        <a href={`tel:${l.phone}`} data-testid={`crm-cred-call-${l.id}`}
                          title="अभी फ़ोन करें"
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-zinc-200 text-zinc-600 text-xs font-medium hover:bg-zinc-50 whitespace-nowrap">
                          <Phone className="w-3.5 h-3.5" /> फ़ोन
                        </a>
                      </div>
                    </td>
                  </tr>
                  {expandedCred === l.id && (
                    <tr className="bg-zinc-50" data-testid={`crm-cred-detail-${l.id}`}>
                      <td colSpan="8" className="px-6 py-4">
                        <div className="text-xs text-zinc-600 space-y-2">
                          <p><span className="font-semibold text-zinc-700">Delivery status:</span> {waStatusBadge(l.wa_status).label} {l.wa_log_at ? `· ${new Date(l.wa_log_at).toLocaleString('hi-IN')}` : ''}</p>
                          <p><span className="font-semibold text-zinc-700">Meta Message ID:</span> <span className="font-mono break-all">{l.wa_message_id || '—'}</span></p>
                          <p className="text-zinc-400">नोट: "Delivered/Read" तभी update होगा जब Meta WhatsApp webhook आपके account में set हो। "Sent" का मतलब Meta ने message स्वीकार कर लिया।</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {(pages > 1 || total > 0) && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 bg-zinc-50">
              <div className="flex items-center gap-3">
                <p className="text-xs text-zinc-500 tabular-nums">{total} leads · Page {page}/{pages}</p>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} data-testid="crm-page-size"
                  className="h-8 px-2 text-xs rounded-md border border-zinc-200 bg-white text-zinc-700">
                  <option value={25}>25 / पेज</option>
                  <option value={50}>50 / पेज</option>
                  <option value={100}>100 / पेज</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="crm-cred-prev-page"
                  className="h-8 px-3 text-sm rounded-md border border-zinc-200 bg-white disabled:opacity-40 hover:bg-zinc-50">‹ पिछला</button>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="crm-cred-next-page"
                  className="h-8 px-3 text-sm rounded-md border border-zinc-200 bg-white disabled:opacity-40 hover:bg-zinc-50">अगला ›</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {isTrash && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-amber-800" data-testid="crm-trash-banner">
              <Trash2 className="w-4 h-4 shrink-0" />
              <span>🗑️ <b>Trash</b> — delete किए गए leads यहाँ हैं। इन्हें <b>Restore</b> करके वापस ला सकते हैं या <b>हमेशा के लिए</b> मिटा सकते हैं। Auto-sync इन्हें वापस नहीं लाएगा।</span>
            </div>
          )}
          {!isTrash && dupGroups.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-hidden" data-testid="crm-duplicates-panel">
              <button onClick={() => setShowDups(v => !v)} data-testid="crm-duplicates-toggle"
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-orange-100/50 transition-colors">
                <GitMerge className="w-4 h-4 text-orange-600 shrink-0" />
                <span className="text-sm font-semibold text-orange-800">
                  🔗 {dupGroups.length} संभावित duplicate समूह — Review & Merge
                </span>
                <span className="text-xs text-orange-600 hidden sm:inline">एक ही नाम वाले leads मिले — एक click में merge करें</span>
                <ChevronDown className={`w-4 h-4 text-orange-500 ml-auto transition-transform ${showDups ? 'rotate-180' : ''}`} />
              </button>
              {showDups && (
                <div className="border-t border-orange-200 divide-y divide-orange-100 max-h-[420px] overflow-auto" data-testid="crm-duplicates-list">
                  {dupGroups.map((g) => (
                    <div key={g.key} className="px-4 py-3 bg-white/60" data-testid={`crm-dup-group-${g.key}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-zinc-900 text-sm">{g.name}</span>
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">{g.count} leads</span>
                        {g.schools.length > 1 && <span className="text-[11px] text-zinc-400">({g.schools.length} अलग स्कूल नाम)</span>}
                        <button onClick={() => mergeGroup(g)} disabled={dupMergingKey === g.key}
                          data-testid={`crm-dup-merge-${g.key}`}
                          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors">
                          <GitMerge className="w-3 h-3" /> {dupMergingKey === g.key ? 'Merge हो रहा…' : 'सबको Merge करें'}
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {g.leads.map((l) => (
                          <button key={l.id} onClick={() => setSelectedId(l.id)} data-testid={`crm-dup-lead-${l.id}`}
                            className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg bg-white border border-zinc-200 hover:border-orange-300 transition-colors">
                            <TempBadge t={l.temperature} />
                            <span className="text-zinc-700">{l.phone || 'बिना फ़ोन'}</span>
                            <span className="text-zinc-400">· {l.school_name || sourceLabel(l.source)} · score {l.lead_score || 0}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-zinc-200 p-3 flex flex-wrap items-center gap-2" data-testid="crm-filter-bar">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search-input"
                placeholder="नाम / फ़ोन / स्कूल / शहर खोजें…"
                className="w-full h-9 pl-9 pr-3 border border-zinc-200 rounded-md text-sm focus:ring-2 focus:ring-zinc-200 focus:border-zinc-300 outline-none" />
            </div>
            <button type="button" data-testid="crm-quick-filter-demo"
              onClick={() => { setStatus(status === 'demo_scheduled' ? '' : 'demo_scheduled'); setPage(1); }}
              className={`h-9 px-3 rounded-md text-sm font-medium border whitespace-nowrap transition-colors ${status === 'demo_scheduled' ? 'bg-orange-500 text-white border-orange-500' : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'}`}>
              🎬 Demo Scheduled{status === 'demo_scheduled' ? ' ✓' : ''}
            </button>
            <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="crm-filter-source"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="crm-filter-status"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              <option value="">सभी Status</option>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={temperature} onChange={(e) => setTemperature(e.target.value)} data-testid="crm-filter-temp"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              <option value="">सभी Temp</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="crm-filter-sort"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              <option value="last_activity_at">हाल की activity</option>
              <option value="lead_score">Score (high→low)</option>
              <option value="deal_value">Deal value</option>
              <option value="created_at">सबसे नए</option>
            </select>
            <select value={engagement} onChange={(e) => { setEngagement(e.target.value); setPage(1); }} data-testid="crm-filter-engagement"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              {ENGAGEMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {engagement && engagement !== 'demo_sent' && (
              <button onClick={bulkDrip} disabled={dripBusy} data-testid="crm-bulk-drip-btn"
                className="h-9 px-3 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap">
                {dripBusy ? '⏳ भेज रहे…' : '📲 सभी को WhatsApp drip भेजो'}
              </button>
            )}
            <select value={dateRange} onChange={(e) => { setDateRange(e.target.value); setPage(1); }} data-testid="crm-filter-daterange"
              className="h-9 px-3 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700">
              <option value="">📅 सभी समय</option>
              <option value="today">आज</option>
              <option value="week">पिछले 1 हफ़्ते</option>
              <option value="15days">पिछले 15 दिन</option>
              <option value="month">पिछले 1 महीने</option>
              <option value="custom">कस्टम तारीख…</option>
            </select>
            {dateRange === 'custom' && (
              <div className="flex items-center gap-1.5" data-testid="crm-filter-custom-dates">
                <input type="date" value={dateFrom} max={dateTo || undefined}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} data-testid="crm-filter-date-from"
                  className="h-9 px-2 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700" />
                <span className="text-zinc-400 text-sm">से</span>
                <input type="date" value={dateTo} min={dateFrom || undefined}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }} data-testid="crm-filter-date-to"
                  className="h-9 px-2 border border-zinc-200 rounded-md text-sm bg-white text-zinc-700" />
              </div>
            )}
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="bg-zinc-900 text-white rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid="crm-bulk-bar">
              <span className="text-sm font-medium tabular-nums">{selected.size} चुने गए</span>
              {isTrash ? (
                <>
                  <button onClick={bulkRestore} disabled={bulkBusy} data-testid="crm-bulk-restore"
                    className="h-8 px-3 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                  <button onClick={bulkPermanentDelete} disabled={bulkBusy} data-testid="crm-bulk-permanent-delete"
                    className="h-8 px-3 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> हमेशा के लिए मिटाएँ</button>
                </>
              ) : (
                <>
              <div className="flex items-center gap-1.5">
                <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} data-testid="crm-bulk-status-select"
                  className="h-8 px-2 rounded-md text-sm text-zinc-900">
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button onClick={applyBulkStatus} disabled={bulkBusy} data-testid="crm-bulk-status-apply"
                  className="h-8 px-3 rounded-md bg-white text-zinc-900 text-sm font-medium hover:bg-zinc-100 disabled:opacity-50">Status बदलें</button>
              </div>
              <div className="flex items-center gap-1.5">
                <input value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} placeholder="tag…" data-testid="crm-bulk-tag-input"
                  className="h-8 px-2 rounded-md text-sm text-zinc-900 w-24" />
                <button onClick={applyBulkTag} disabled={bulkBusy || !bulkTag.trim()} data-testid="crm-bulk-tag-apply"
                  className="h-8 px-3 rounded-md border border-zinc-600 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">Tag जोड़ें</button>
              </div>
              <div className="flex items-center gap-1.5">
                <select value={bulkTemplate} onChange={(e) => setBulkTemplate(e.target.value)} data-testid="crm-bulk-template-select"
                  className="h-8 px-2 rounded-md text-sm text-zinc-900 max-w-[170px]">
                  <option value="">WhatsApp template…</option>
                  {waTemplates.map(t => <option key={t.name} value={t.name}>{(t.name || '').replace(/_/g, ' ')}</option>)}
                </select>
                <button onClick={applyBulkTemplate} disabled={tplBulkBusy || bulkBusy || !bulkTemplate} data-testid="crm-bulk-template-send"
                  className="h-8 px-3 rounded-md bg-green-500 text-white text-sm font-medium hover:bg-green-600 disabled:opacity-50 inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" /> {tplBulkBusy ? 'भेजा जा रहा है…' : 'भेजें'}</button>
              </div>
              <button onClick={bulkDelete} disabled={bulkBusy} data-testid="crm-bulk-delete"
                className="h-8 px-3 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              {selected.size >= 2 && (
                <button onClick={mergeLeads} disabled={bulkBusy} data-testid="crm-bulk-merge"
                  className="h-8 px-3 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1"><GitMerge className="w-3.5 h-3.5" /> Merge ({selected.size})</button>
              )}
                </>
              )}
              <button onClick={() => setSelected(new Set())} className="text-sm text-zinc-400 hover:text-white ml-auto inline-flex items-center gap-1" data-testid="crm-bulk-clear"><X className="w-3.5 h-3.5" /> Clear</button>
            </div>
          )}

          {viewMode !== 'trash' && total > leads.length && (
            <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-indigo-50 border border-indigo-200 text-sm" data-testid="crm-selectall-banner">
              <span className="text-indigo-800">
                {selected.size > 0 && selected.size >= leads.length
                  ? `${selected.size} leads चुने गए।`
                  : `इस page पर ${leads.length} दिख रहे · कुल ${total} match करते हैं।`}
              </span>
              <button onClick={selectAllFiltered} disabled={selectingAll} data-testid="crm-select-all-filtered"
                className="font-semibold text-indigo-700 hover:underline disabled:opacity-50">
                {selectingAll ? 'चुन रहे…' : `इस filter के सभी ${total} चुनें →`}
              </button>
            </div>
          )}

          {/* Leads table */}
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" data-testid="crm-select-all"
                        checked={leads.length > 0 && selected.size === leads.length}
                        onChange={toggleSelectAll} className="rounded border-zinc-300" />
                    </th>
                    {[
                      { label: 'Lead' }, { label: 'Source' }, { label: 'AI Summary' },
                      { label: 'Score', field: 'lead_score' }, { label: 'Temp' },
                      { label: 'Deal ₹', field: 'deal_value' }, { label: 'Status' },
                      { label: 'Created', field: 'created_at' },
                      { label: 'Last Activity', field: 'last_activity_at' }, { label: 'Action' },
                    ].map(({ label, field }) => (
                      <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-500 uppercase tracking-wider whitespace-nowrap">
                        {field ? (
                          <button onClick={() => toggleSort(field)} data-testid={`crm-sort-${field}`}
                            className="inline-flex items-center gap-1 uppercase hover:text-zinc-800 transition-colors">
                            {label}
                            {sort === field
                              ? (order === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                              : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                          </button>
                        ) : label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {loading ? (
                    <tr><td colSpan="11" className="px-4 py-12 text-center"><div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-zinc-200 border-t-zinc-500"></div></td></tr>
                  ) : leads.length === 0 ? (
                    <tr><td colSpan="11" className="px-4 py-12 text-center text-zinc-400 text-sm" data-testid="crm-empty">कोई lead नहीं मिला</td></tr>
                  ) : leads.map((l) => (
                    <tr key={l.id} onClick={() => setSelectedId(l.id)} data-testid={`crm-lead-row-${l.id}`}
                      className={`hover:bg-zinc-50 cursor-pointer transition-colors ${selected.has(l.id) ? 'bg-zinc-50' : ''}`}>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(l.id)} onChange={(e) => toggleSelect(l.id, e)}
                          data-testid={`crm-select-${l.id}`} className="rounded border-zinc-300" />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-zinc-900 text-sm">{l.name || 'Lead'}</p>
                        <p className="text-xs text-zinc-500 tabular-nums">{l.phone || '—'}</p>
                        {l.school_name && <p className="text-[11px] text-zinc-400 truncate max-w-[180px] flex items-center gap-1"><Building2 className="w-3 h-3 shrink-0" /> {l.school_name}</p>}
                        {l.demo_scheduled_label && (
                          <p className="text-[11px] font-semibold text-orange-600 flex items-center gap-1 mt-0.5" data-testid={`crm-demo-time-${l.id}`}>
                            🎬 <span>{l.demo_scheduled_label}</span>
                          </p>
                        )}
                        {l.demo_sent && <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 font-medium" data-testid={`crm-demo-sent-${l.id}`}><span className="w-1 h-1 rounded-full bg-emerald-500" /> Demo भेजा</span>}
                        {l.engagement_stage && l.engagement_stage !== 'demo_sent' && ENGAGEMENT_META[l.engagement_stage] && (
                          <span data-testid={`crm-engagement-${l.id}`}
                            className={`inline-flex items-center gap-1 mt-1 ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ENGAGEMENT_META[l.engagement_stage].cls}`}>
                            {ENGAGEMENT_META[l.engagement_stage].label}
                          </span>
                        )}
                        {l.drip_status === 'active' && (
                          <span data-testid={`crm-drip-status-${l.id}`} title="31-दिन WhatsApp drip में"
                            className="inline-flex items-center gap-1 mt-1 ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-teal-50 text-teal-700 border border-teal-200">
                            📲 Drip Day {l.drip_day || 1}/31
                          </span>
                        )}
                        {l.drip_status === 'completed' && (
                          <span data-testid={`crm-drip-status-${l.id}`} title="Drip पूरा हुआ"
                            className="inline-flex items-center gap-1 mt-1 ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-zinc-100 text-zinc-500 border border-zinc-200">
                            📲 Drip ✓
                          </span>
                        )}
                        {(l.tags || []).length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">{(l.tags || []).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500">#{t}</span>)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-md bg-zinc-100 text-zinc-600 whitespace-nowrap">{sourceLabel(l.source)}</span></td>
                      <td className="px-4 py-3 min-w-[380px] max-w-[440px] align-top" onClick={(e) => e.stopPropagation()} data-testid={`crm-ai-summary-${l.id}`}>
                        {(() => {
                          const s = summaries[l.id] || (l.ai_chat_summary ? { summary: l.ai_chat_summary, interest: l.ai_summary_interest, next_step: l.ai_summary_next_step, has_chat: true } : undefined);
                          const interestColor = (iv) => /गर्म|hot/i.test(iv || '') ? 'bg-rose-50 text-rose-700 border-rose-100' : /गुनगुना|warm/i.test(iv || '') ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-zinc-100 text-zinc-500 border-zinc-200';
                          if (!s) return <span className="text-xs text-zinc-400 italic" data-testid={`crm-ai-summary-loading-${l.id}`}>AI सारांश बन रहा है…</span>;
                          if (s.has_chat === false) return <span className="text-xs text-zinc-300">— कोई chat नहीं</span>;
                          if (s.error || !s.summary) return <span className="text-xs text-zinc-300">सारांश उपलब्ध नहीं</span>;
                          return (
                            <div>
                              {s.interest && <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full border mb-1 ${interestColor(s.interest)}`}>{s.interest}</span>}
                              <p className="text-xs text-zinc-600 leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.summary}</p>
                              {s.next_step && (
                                <p className="text-[11px] text-emerald-700 font-medium mt-1 leading-snug" data-testid={`crm-ai-nextstep-${l.id}`}>👉 {s.next_step}</p>
                              )}
                              <button onClick={() => setSelectedId(l.id)} className="mt-0.5 text-[11px] font-medium text-zinc-700 hover:text-zinc-900" data-testid={`crm-know-more-${l.id}`}>Know more →</button>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3"><ScoreRing score={l.lead_score || 0} /></td>
                      <td className="px-4 py-3"><TempBadge t={l.temperature} /></td>
                      <td className="px-4 py-3 text-sm font-medium text-zinc-900 tabular-nums">{inr(l.deal_value)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <select value={l.status} disabled={rowBusyId === l.id}
                          onChange={(e) => quickChangeStatus(l, e.target.value, e)}
                          data-testid={`crm-row-status-${l.id}`}
                          className="text-xs h-8 px-2 rounded-md font-medium border border-zinc-200 bg-white text-zinc-700 cursor-pointer">
                          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums whitespace-nowrap" data-testid={`crm-row-created-${l.id}`}>{l.created_at ? new Date(l.created_at).toLocaleDateString('hi-IN') : '—'}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500 tabular-nums">{l.last_activity_at ? new Date(l.last_activity_at).toLocaleDateString('hi-IN') : '—'}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          {isTrash ? (
                            <>
                              <button onClick={(e) => restoreLead(l, e)} disabled={rowBusyId === l.id}
                                data-testid={`crm-restore-lead-${l.id}`} title="Lead वापस लाएँ"
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap">
                                <RotateCcw className="w-3.5 h-3.5" /> Restore
                              </button>
                              <button onClick={(e) => permanentDeleteLead(l, e)} disabled={rowBusyId === l.id}
                                data-testid={`crm-permanent-delete-${l.id}`} title="हमेशा के लिए delete करें"
                                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                          <div className="relative">
                            <button onClick={(e) => { e.stopPropagation(); setTplMenuId(tplMenuId === l.id ? null : l.id); }}
                              disabled={demoBusyId === l.id || tplBusyId === l.id}
                              data-testid={`crm-send-menu-${l.id}`}
                              title="WhatsApp message भेजें — Demo या कोई भी approved template"
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800 disabled:opacity-50 whitespace-nowrap">
                              {(demoBusyId === l.id || tplBusyId === l.id) ? <span className="animate-spin">…</span> : <Send className="w-3.5 h-3.5" />}
                              {l.demo_sent ? 'फिर भेजें' : 'भेजें'} <span className="text-[9px] opacity-70">▾</span>
                            </button>
                            {tplMenuId === l.id && (
                              <div className="absolute right-0 z-30 mt-1 w-64 bg-white border border-zinc-200 rounded-lg shadow-xl py-1 text-left max-h-72 overflow-y-auto" data-testid={`crm-send-menu-list-${l.id}`}>
                                <button onClick={(e) => { setTplMenuId(null); sendDemo(l, e); }}
                                  data-testid={`crm-send-demo-${l.id}`}
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 flex items-center gap-2 border-b border-zinc-100">
                                  📲 <span>Demo credentials <span className="text-zinc-400">(14-दिन trial)</span></span>
                                </button>
                                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-zinc-400">WhatsApp Templates</div>
                                {waTemplates.length === 0 && (
                                  <div className="px-3 py-2 text-[11px] text-zinc-400">कोई approved template नहीं मिला</div>
                                )}
                                {waTemplates.map((t) => (
                                  <button key={t.name} onClick={(e) => { e.stopPropagation(); setTplMenuId(null); sendTemplate(l, t.name); }}
                                    data-testid={`crm-send-tpl-${l.id}-${t.name}`}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 flex items-center justify-between gap-2">
                                    <span className="truncate">{t.name.replace(/_/g, ' ')}</span>
                                    <span className="text-[9px] text-zinc-400 uppercase">{t.category}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <button onClick={(e) => deleteLead(l, e)} disabled={rowBusyId === l.id}
                            data-testid={`crm-delete-lead-${l.id}`} title="Lead delete करें"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(pages > 1 || total > 0) && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 bg-zinc-50">
                <div className="flex items-center gap-3">
                  <p className="text-xs text-zinc-500 tabular-nums">{total} leads · Page {page}/{pages}</p>
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} data-testid="crm-page-size"
                    className="h-8 px-2 text-xs rounded-md border border-zinc-200 bg-white text-zinc-700">
                    <option value={25}>25 / पेज</option>
                    <option value={50}>50 / पेज</option>
                    <option value={100}>100 / पेज</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="crm-prev-page"
                    className="h-8 px-3 text-sm rounded-md border border-zinc-200 bg-white disabled:opacity-40 hover:bg-zinc-50">‹ पिछला</button>
                  <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="crm-next-page"
                    className="h-8 px-3 text-sm rounded-md border border-zinc-200 bg-white disabled:opacity-40 hover:bg-zinc-50">अगला ›</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {selectedId && (
        <LeadDetailDrawer token={token} leadId={selectedId}
          onClose={() => setSelectedId(null)} onChanged={onLeadChanged} />
      )}
      {showAutomation && <AutomationPanel token={token} onClose={() => setShowAutomation(false)} />}
    </div>
  );
}
